import {
  CommonHelperFunctions,
  DashboardState,
  ESdChangeReason,
  IAction,
  IComponent,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IEditability,
  IEvent,
  IProperty,
  IPropertyDefinition,
  IValidation,
  IVisibility
} from "@kuika/kuika-cl-model";
import {
  IApplyPropertyGridChangesToDisplayParameters,
  IChangeSelectedPropertyTypeParameters
} from "../../../models/interface/function-parameters";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import type { SaveActionToEventResult } from "../../../shared/utility/property-sd-helper";
import { ScreenDesignHelper } from "../../../shared/utility/screen-design-helper";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface PropertyProviderState {
  applyPropertyGridChangesToDisplay: (
    params: IApplyPropertyGridChangesToDisplayParameters,
    changeReason: ESdChangeReason
  ) => void;
  changeSelectedPropertyType: (params: IChangeSelectedPropertyTypeParameters) => void;
  getPropertyDefinition: (
    diComponent: IDisplayItemComponent,
    diProperty: IDisplayItemProperty
  ) => IPropertyDefinition | undefined;
  getPropertyDefinitionByComponentName: (
    componentName: string,
    diProperty: IDisplayItemProperty
  ) => IPropertyDefinition | undefined;
  saveActionToEvent: (
    kuikaId: string,
    action: IAction,
    eventName: IEvent,
    changeReason?: ESdChangeReason
  ) => SaveActionToEventResult;
  saveValidationToIValidation: (
    kuikaId: string,
    validation: IValidation,
    changeReason?: ESdChangeReason | undefined
  ) => void;
  saveVisibilitytoDisplayItem: (
    kuikaId: string,
    visibility: IVisibility,
    changeReason?: ESdChangeReason | undefined
  ) => void;
  saveEditabilityToDisplayItem: (
    kuikaId: string,
    editability: IEditability,
    changeReason?: ESdChangeReason | undefined
  ) => void;
  deleteActionFromEvent: (kuikaId: string, action: IAction, eventName: IEvent) => void;
  deleteValidateFromIValidation: (kuikaId: string, validation: IValidation) => void;
  addEventToDi: (kuikaId: string, event: IEvent) => void;
  expandedProperties: string[];
  addItemToExpandedPropertyList: (propertyName: string) => void;
  removeItemFromExpandedPropertyList: (propertyName: string) => void;
}

export class PropertyProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  PropertyProviderState
> {
  static defaultState: PropertyProviderState = {
    applyPropertyGridChangesToDisplay: () => {},
    changeSelectedPropertyType: () => {},
    getPropertyDefinition: () => {
      return undefined;
    },
    getPropertyDefinitionByComponentName: () => {
      return undefined;
    },
    saveActionToEvent: () => ({
      owner: "dropped-component-missing",
      attachedToComponent: false,
      dashboardState: DashboardState.design
    }),
    saveValidationToIValidation: () => {},
    saveVisibilitytoDisplayItem: () => {},
    saveEditabilityToDisplayItem: () => {},
    deleteActionFromEvent: () => {},
    deleteValidateFromIValidation: () => {},
    addEventToDi: () => {},
    expandedProperties: [],
    addItemToExpandedPropertyList: () => {},
    removeItemFromExpandedPropertyList: () => {}
  };

  getProviderValues = (): PropertyProviderState => {
    return {
      applyPropertyGridChangesToDisplay: this.applyPropertyGridChangesToDisplay,
      changeSelectedPropertyType: this.changeSelectedPropertyType,
      getPropertyDefinition: this.getPropertyDefinition,
      getPropertyDefinitionByComponentName: this.getPropertyDefinitionByComponentName,
      saveActionToEvent: this.saveActionToEvent,
      saveValidationToIValidation: this.saveValidationToIValidation,
      saveVisibilitytoDisplayItem: this.saveVisibilitytoDisplayItem,
      saveEditabilityToDisplayItem: this.saveEditabilityToDisplayItem,
      deleteActionFromEvent: this.deleteActionFromEvent,
      deleteValidateFromIValidation: this.deleteValidateFromIValidation,
      addEventToDi: this.addEventToDi,
      expandedProperties: this.state.expandedProperties,
      addItemToExpandedPropertyList: this.addItemToExpandedPropertyList,
      removeItemFromExpandedPropertyList: this.removeItemFromExpandedPropertyList
    };
  };

  addItemToExpandedPropertyList = (propertyName: string) => {
    let expandedProperties = this.state.expandedProperties.filter((name) => name !== propertyName);
    if (expandedProperties.length > 100) {
      expandedProperties = expandedProperties.splice(0, 1);
    }
    expandedProperties.push(propertyName);
    this.setState({
      expandedProperties,
      changeReason: ESdChangeReason.ExpandedProperties
    });
  };

  removeItemFromExpandedPropertyList = (propertyName: string) => {
    const expandedProperties = this.state.expandedProperties.filter((name) => name !== propertyName);
    this.setState({ expandedProperties });
  };

  saveActionToEvent = (
    kuikaId: string,
    action: IAction,
    event: IEvent,
    changeReason?: ESdChangeReason
  ): SaveActionToEventResult => {
    return this.partial.propertySdHelper.saveActionToEvent(kuikaId, action, event, changeReason);
  };

  saveValidationToIValidation = (
    kuikaId: string,
    validation: IValidation,
    changeReason?: ESdChangeReason | undefined
  ) => {
    this.partial.propertySdHelper.saveValidationToIValidation(kuikaId, validation, changeReason);
  };

  saveVisibilitytoDisplayItem = (
    kuikaId: string,
    visibility: IVisibility,
    changeReason?: ESdChangeReason | undefined
  ) => {
    this.partial.propertySdHelper.saveVisibilitytoDisplayItem(kuikaId, visibility, changeReason);
  };

  saveEditabilityToDisplayItem = (
    kuikaId: string,
    editability: IEditability,
    changeReason?: ESdChangeReason | undefined
  ) => {
    this.partial.propertySdHelper.saveEditabilityToDisplayItem(kuikaId, editability, changeReason);
  };

  deleteActionFromEvent = (kuikaId: string, action: IAction, event: IEvent) => {
    this.partial.propertySdHelper.deleteActionFromEvent(kuikaId, action, event);
  };

  deleteValidateFromIValidation = (kuikaId: string, validation: IValidation) => {
    this.partial.propertySdHelper.deleteValidationToIValidation(kuikaId, validation);
  };

  addEventToDi = (kuikaId: string, event: IEvent) => {
    this.partial.propertySdHelper.addEventToDi(kuikaId, event);
  };

  applyPropertyGridChangesToDisplay = (
    params: IApplyPropertyGridChangesToDisplayParameters,
    changeReason: ESdChangeReason
  ): void => {
    this.partial.propertySdHelper.applyPropertyGridChangesToDisplay(params, changeReason);
  };

  changeSelectedPropertyType = (params: IChangeSelectedPropertyTypeParameters) => {
    this.partial.propertySdHelper.changeSelectedPropertyType(params);
  };

  getPropertyDefinition = (
    diComponent: IDisplayItemComponent,
    diProperty: IDisplayItemProperty
  ): IPropertyDefinition | undefined => {
    let result: IPropertyDefinition | undefined;
    if (this.state.project && this.state.project.componentSetObj && this.state.project.componentSetObj.components) {
      const component: IComponent | undefined = ScreenDesignHelper.findComponentById(
        diComponent.id,
        this.state.project.componentSetObj.components
      );
      if (component) {
        const property = component.properties.find((p) => p.name === diProperty.name);
        if (property) {
          result = this.getPropertyDefinitionByProperty(property);
          return result;
        }
      } else if (diComponent && diComponent.name) {
        return this.getPropertyDefinitionByComponentName(diComponent.name, diProperty);
      }
    }
  };

  getPropertyDefinitionByComponentName = (
    componentName: string,
    diProperty: IDisplayItemProperty
  ): IPropertyDefinition | undefined => {
    let result: IPropertyDefinition | undefined;
    if (this.state.project && this.state.project.componentSetObj && this.state.project.componentSetObj.components) {
      const component: IComponent | undefined = CommonHelperFunctions.findComponentByName(
        componentName,
        this.state.project.componentSetObj.components
      );
      if (component) {
        const componentProperty = component.properties.find((p) => p.name === diProperty.name);
        if (componentProperty) {
          result = this.getPropertyDefinitionByProperty(componentProperty);
          return result;
        }
      }
    }
  };

  getPropertyDefinitionByProperty = (property: IProperty): IPropertyDefinition => {
    const propertyDefinition: IPropertyDefinition = {
      description: property.description,
      hasOptions: property.hasOptions,
      isRequired: property.isRequired,
      isTechnical: property.isTechnical,
      isLanguageOptionsProp: property.isLanguageOptionsProp,
      // isEvent: property.isEvent,
      isVisibleInFieldPicker: property.isVisibleInFieldPicker,
      isValueProp: property.isValueProp,
      isOptionsProp: property.isOptionsProp,
      isTableOptionsProp: property.isTableOptionsProp,
      isGoogleMapsOptionsProp: property.isGoogleMapsOptionsProp,
      isEditabilityProp: property.isEditabilityProp,
      isVisibleProp: property.isVisibleProp,
      isAdvanced: property.isAdvanced,
      isIgnoredOnCodeOutput: property.isIgnoredOnCodeOutput,
      isIgnoredOnGeneratedApp: property.isIgnoredOnGeneratedApp,
      isSupportMultiLanguage: property.isSupportMultiLanguage,
      appSupportType: property.appSupportType,
      isDeprecated: property.isDeprecated,
      name: property.name,
      options: property.options,
      tagName: property.tagName,
      type: property.type,
      typeOptions: property.typeOptions,
      defaultValue: property.defaultValue,
      isChartOptionsProp: property.isChartOptionsProp,
      isTimelineOptionsProp: property.isTimelineOptionsProp,
      isCalendarOptionsProp: property.isCalendarOptionsProp,
      isMenuOptionsProp: property.isMenuOptionsProp,
      isBreadcrumbOptionsProp: property.isBreadcrumbOptionsProp,
      isRadialChartOptionsProp: property.isRadialChartOptionsProp,
      isRadialChartLevelOptionsProp: property.isRadialChartLevelOptionsProp,
      isMixedChartOptionsProp: property.isMixedChartOptionsProp,
      isMixedChartLineOptionsProp: property.isMixedChartLineOptionsProp,
      isMixedChartPointOptionsProp: property.isMixedChartPointOptionsProp,
      isMixedChartBarOptionsProp: property.isMixedChartBarOptionsProp,
      isKanbanBoardLaneOptionsProp: property.isKanbanBoardLaneOptionsProp,
      isKanbanBoardCardOptionsProp: property.isKanbanBoardCardOptionsProp,
      isNodeTreeNodeOptionsProp: property.isNodeTreeNodeOptionsProp,
      isChatOptionsProp: property.isChatOptionsProp,
      isTreeOptionsProp: property.isTreeOptionsProp,
      isDirectoryOptionsProp: property.isDirectoryOptionsProp,
      isSpecificDatesProp: property.isSpecificDatesProp,
      inVisibleStyles: property.inVisibleStyles,
      hideFixedValue: property.hideFixedValue,
      isTagsInput: property.isTagsInput
    };
    return propertyDefinition;
  };
}
